import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import PlusIcon from "../../svg/PlusIcon";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ServiceSearchBar from "./ServiceSearchBar";
import ServiceTypeFilter from "./ServiceTypeFilter";
import ServiceDetailsView from "./ServiceDetailsView";
import ServiceListItem from "./ServiceListItem";
import ServiceDetailsSkeleton from "../skeletons/ServiceDetailsSkeleton";
import { Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import type { ServiceDetails } from "./types";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";

const SavedServicesScreen = ({
  savedServices,
  setOpenModal,
  serviceFilter,
  setServiceFilter,
  serviceTypeFilter,
  setServiceTypeFilter,
  loadData,
  setEditingService,
  setIsEditMode,
  serviceToSelect,
  onServiceSelected,
}: {
  savedServices: ServiceDetails[];
  setOpenModal: (open: boolean) => void;
  serviceFilter: string;
  setServiceFilter: (filter: string) => void;
  serviceTypeFilter: number | null;
  setServiceTypeFilter: (filter: number | null) => void;
  loadData: () => Promise<void>;
  setEditingService: (service: ServiceDetails) => void;
  setIsEditMode: (isEdit: boolean) => void;
  serviceToSelect?: ServiceDetails;
  onServiceSelected?: () => void;
}) => {
  const theme: Theme = useTheme();
  const isLoading = useSelector(
    (state: RootStateServiceLogicBuilder) => state.serviceLogicBuilder.loading
  );
  const [selectedService, setSelectedService] = useState<ServiceDetails | null>(
    savedServices[0] || null
  );

  // Update selectedService when savedServices changes
  React.useEffect(() => {
    if (savedServices.length > 0) {
      // If there's a specific service to select (from save/edit), prioritize it
      if (serviceToSelect) {
        const serviceToSelectFromList = savedServices.find(
          (s) => s.id === serviceToSelect.id
        );
        if (serviceToSelectFromList) {
          setSelectedService(serviceToSelectFromList);
          // Clear the serviceToSelect flag
          onServiceSelected?.();
          return;
        }
      }

      if (!selectedService) {
        // If no service is selected, select the first one
        setSelectedService(savedServices[0]);
      } else {
        // If a service is selected, find the updated version in the new data
        const updatedService = savedServices.find(
          (s) => s.id === selectedService.id
        );
        // Only update if the service object reference has changed
        if (updatedService && updatedService !== selectedService) {
          // Update with the fresh data from the server
          setSelectedService(updatedService);
        } else if (!updatedService) {
          // If the selected service no longer exists (filtered out), select the first available one
          setSelectedService(savedServices[0]);
        }
      }
    } else {
      // No services available after filtering, clear selection
      setSelectedService(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedServices, serviceToSelect]);

  return (
    <div style={styles.savedServicesLayout}>
      {/* Left Side - Services List */}
      <div style={styles.leftPanel}>
        <ServiceSearchBar
          serviceFilter={serviceFilter}
          setServiceFilter={setServiceFilter}
          theme={theme}
        />

        <div style={styles.servicesWrapper(theme)}>
          <ServiceTypeFilter
            serviceTypeFilter={serviceTypeFilter}
            setServiceTypeFilter={setServiceTypeFilter}
            theme={theme}
          />
          <ScrollableWrapper customPadding={"0"}>
            {isLoading ? (
              <div style={styles.servicesContainer}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    sx={{
                      width: "100%",
                      height: "49px", // Same height as ServiceListItem (padding: 12px + content + padding: 12px + border)
                      bgcolor: theme.colorTokens.N[30],
                      borderRadius: "8px",
                      marginBottom: index < 2 ? "8px" : "0px", // Match gap from servicesContainer
                    }}
                    animation="pulse"
                  />
                ))}
              </div>
            ) : savedServices.length > 0 ? (
              <div style={styles.servicesContainer}>
                {savedServices.map((service, index) => (
                  <ServiceListItem
                    key={service.id || index}
                    service={service}
                    isSelected={selectedService?.id === service.id}
                    onClick={() => setSelectedService(service)}
                    theme={theme}
                  />
                ))}
              </div>
            ) : (
              <div style={styles.emptyServicesState}>
                <div style={styles.emptyStateTitle(theme)}>
                  No services match your filters
                </div>
                <div style={styles.emptyStateText(theme)}>
                  Try adjusting your search or filter criteria to find services
                </div>
              </div>
            )}
          </ScrollableWrapper>
        </div>
        <PrimaryButton
          label={"New Service"}
          icon={<PlusIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />}
          onClick={() => setOpenModal(true)}
          customLabelStyles={{ textAlign: "left" }}
        />
      </div>

      {/* Right Side - Service Details */}
      <div style={styles.rightPanel}>
        <ScrollableWrapper customPadding={"0"}>
          {isLoading ? (
            <ServiceDetailsSkeleton theme={theme} />
          ) : selectedService ? (
            <ServiceDetailsView
              key={selectedService.id}
              service={selectedService}
              theme={theme}
              loadData={loadData}
              savedServices={savedServices}
              setSelectedService={setSelectedService}
              setEditingService={setEditingService}
              setIsEditMode={setIsEditMode}
              setOpenModal={setOpenModal}
            />
          ) : (
            <div style={styles.noSelectionState}>
              <div style={styles.noSelectionTitle(theme)}>
                No service selected
              </div>
              <div style={styles.noSelectionText(theme)}>
                {savedServices.length === 0
                  ? "No services match your current filters"
                  : "Click a service from the list to view details"}
              </div>
            </div>
          )}
        </ScrollableWrapper>
      </div>
    </div>
  );
};

const styles = {
  savedServicesLayout: {
    display: "flex",
    gap: "24px",
    height: "100%", // Fill parent container
    width: "100%",
  },
  leftPanel: {
    width: "300px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    height: "100%",
  },
  servicesWrapper: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    flexGrow: 1,
    minHeight: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden" as "hidden",
  }),
  servicesContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    padding: "12px 8px 12px 18px",
  },
  rightPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0, // Important for ScrollableWrapper to work properly
    overflow: "hidden", // Let ScrollableWrapper handle overflow
  },
  noSelectionState: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "32px 16px",
    textAlign: "center" as "center",
  },
  noSelectionTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "8px",
    lineHeight: "24px",
  }),
  noSelectionText: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "20px",
    maxWidth: "280px",
  }),
  emptyServicesState: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    height: "200px",
    padding: "32px 16px",
    textAlign: "center" as "center",
  },
  emptyStateTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "8px",
    lineHeight: "24px",
  }),
  emptyStateText: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "20px",
    maxWidth: "280px",
  }),
};

export default SavedServicesScreen;
